import * as React from 'react';
import styled from '@emotion/styled';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { Auth0ContextInterface } from '@auth0/auth0-react/src/auth0-context';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { PractitionerOrOrganization } from '../../../../../../../../interfaces/interfaces';
import SuggestEditSuccessResponse from './SuggestEditSuccessResponse';
import SuggestEditErrorResponse from './SuggestEditErrorResponse';

const StyledButton = styled(Button)`
  padding: 0.5rem 2rem;
  margin-bottom: 1rem;
`;

const StyledModalBodyContainer = styled.div`
  width: 100%;
`;

const StyledTextField = styled(TextField)`
  margin-bottom: 1rem;
`;

const StyledButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledTitle = styled.h2`
  margin-top: 0;
`;

const StyledCloseButton = styled(Button)`
  padding: 0.5rem 2rem;
  margin-bottom: 1rem;
`;

interface SuggestEditModalBodyProps {
  value: PractitionerOrOrganization | undefined;
  handleClose(): void;
}

interface SubmitAccuracyBody {
  provider_id: string;
  provider_type: string;
  message: string;
  email: string;
  contact: string;
}

const SuggestEditModalBody = ({ value, handleClose }: SuggestEditModalBodyProps) => {
  const [valueInput, setInputValue] = React.useState('');
  const [valueMessage, setValueMessage] = React.useState({ message: '' });
  const [isError, setIsError] = React.useState(false);
  const [statusResponse, setStatusResponse] = React.useState(false);
  const didMount = React.useRef(false);

  const { name, id: provider_id = '', resourceType: provider_type = '' } = value ?? {};
  const { user, getAccessTokenSilently }: Auth0ContextInterface = useAuth0();
  const { email = '', name: contact = '' } = user ?? {};

  const SubmitAccuracyData = React.useMemo<SubmitAccuracyBody>(() => {
    return {
      provider_id,
      provider_type,
      message: valueMessage.message,
      email,
      contact,
    };
  }, [provider_id, provider_type, valueMessage, email, contact]);

  const handleChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value.length <= 200) {
      setInputValue(event.target.value);
    }
  }, []);

  React.useEffect(() => {
    if (didMount.current) {
      const loadSuggestAnEdit = async () => {
        const accessToken = await getAccessTokenSilently();
        setIsError(false);
        try {
          const { status } = await axios.post(`${process.env.REACT_APP_URL_HOST}/v1/accuracy_reports`, SubmitAccuracyData, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });
          if (status >= 200 && status < 300) setStatusResponse(true);
        } catch (error: any) {
          setIsError(true);
        }
      };
      loadSuggestAnEdit().catch((e) => console.error(e.message)); // eslint-disable-line no-console
    } else {
      didMount.current = true;
    }
    return () => {
      setInputValue('');
      setStatusResponse(false);
    };
  }, [valueMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <StyledModalBodyContainer>
      {isError && <SuggestEditErrorResponse />}
      {statusResponse && (
        <SuggestEditSuccessResponse>
          <StyledButtonContainer>
            <StyledCloseButton variant="outlined" onClick={handleClose}>
              Close
            </StyledCloseButton>
          </StyledButtonContainer>
        </SuggestEditSuccessResponse>
      )}
      {!statusResponse && (
        <>
          <StyledTitle>Suggest an edit</StyledTitle>
          {name}
          <h4>Message:</h4>
          <StyledTextField id="outlined-multiline-static" multiline rows={4} value={valueInput} onChange={handleChange} fullWidth />
          {200 - valueInput.length} characters left
          <p>
            Please note that we will share your name and email with the healthcare directory team to track and communicate the status of your
            suggested edit.
          </p>
          <StyledButtonContainer>
            <StyledButton variant="contained" disabled={!valueInput.length} onClick={() => setValueMessage({ ...valueMessage, message: valueInput })}>
              Submit
            </StyledButton>
          </StyledButtonContainer>
        </>
      )}
    </StyledModalBodyContainer>
  );
};

export default SuggestEditModalBody;
